import { useState, useEffect } from "react";
import { API } from "@/App";

// Major airports with coordinates
const AIRPORTS = [
  { code: "JFK", city: "New York", lat: 40.6413, lng: -73.7781 },
  { code: "LAX", city: "Los Angeles", lat: 33.9425, lng: -118.4081 },
  { code: "ORD", city: "Chicago", lat: 41.9742, lng: -87.9073 },
  { code: "MIA", city: "Miami", lat: 25.7959, lng: -80.2870 },
  { code: "SFO", city: "San Francisco", lat: 37.6213, lng: -122.3790 },
  { code: "ATL", city: "Atlanta", lat: 33.6407, lng: -84.4277 },
  { code: "DFW", city: "Dallas", lat: 32.8998, lng: -97.0403 },
  { code: "DEN", city: "Denver", lat: 39.8561, lng: -104.6737 },
  { code: "SEA", city: "Seattle", lat: 47.4502, lng: -122.3088 },
  { code: "BOS", city: "Boston", lat: 42.3656, lng: -71.0096 },
  { code: "IAH", city: "Houston", lat: 29.9902, lng: -95.3368 },
  { code: "MSP", city: "Minneapolis", lat: 44.8848, lng: -93.2223 },
  { code: "DTW", city: "Detroit", lat: 42.2162, lng: -83.3554 },
  { code: "PHL", city: "Philadelphia", lat: 39.8729, lng: -75.2437 },
  { code: "LHR", city: "London", lat: 51.4700, lng: -0.4543 },
  { code: "CDG", city: "Paris", lat: 49.0097, lng: 2.5479 },
  { code: "FCO", city: "Rome", lat: 41.8003, lng: 12.2389 },
  { code: "SYD", city: "Sydney", lat: -33.9461, lng: 151.1772 },
  { code: "NRT", city: "Tokyo", lat: 35.7720, lng: 140.3929 },
  { code: "DXB", city: "Dubai", lat: 25.2532, lng: 55.3657 },
  { code: "YYZ", city: "Toronto", lat: 43.6777, lng: -79.6248 },
  { code: "MEX", city: "Mexico City", lat: 19.4361, lng: -99.0719 },
];

function getDistance(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function findNearestAirport(lat, lng) {
  let nearest = AIRPORTS[0];
  let minDist = Infinity;
  for (const ap of AIRPORTS) {
    const d = getDistance(lat, lng, ap.lat, ap.lng);
    if (d < minDist) {
      minDist = d;
      nearest = ap;
    }
  }
  return nearest;
}

export function useNearbyAirport(user) {
  const [airport, setAirport] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If user has a saved home airport, use it
    if (user?.home_airport) {
      const ap = AIRPORTS.find((a) => a.code === user.home_airport) || {
        code: user.home_airport,
        city: user.home_airport,
      };
      setAirport(ap);
      fetchDeals(ap.code);
      return;
    }

    // Otherwise, try geolocation
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const ap = findNearestAirport(pos.coords.latitude, pos.coords.longitude);
          setAirport(ap);
          fetchDeals(ap.code);
        },
        () => {
          // Geolocation denied — default to JFK
          const fallback = AIRPORTS[0];
          setAirport(fallback);
          fetchDeals(fallback.code);
        },
        { timeout: 5000 }
      );
    } else {
      const fallback = AIRPORTS[0];
      setAirport(fallback);
      fetchDeals(fallback.code);
    }
  }, [user?.home_airport]);

  const fetchDeals = async (code) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/deals/nearby/flights?origin=${code}`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setDeals(data.deals || []);
      }
    } catch (e) {
      console.error("Failed to fetch nearby deals:", e);
    } finally {
      setLoading(false);
    }
  };

  const updateAirport = async (code) => {
    const ap = AIRPORTS.find((a) => a.code === code) || { code, city: code };
    setAirport(ap);
    fetchDeals(code);
    // Save to profile (best-effort — failure doesn't block the UI)
    try {
      await fetch(`${API}/auth/airport`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ airport_code: code }),
      });
    } catch (e) {
      console.error("Failed to save airport preference:", e);
    }
  };

  return { airport, deals, loading, updateAirport, AIRPORTS };
}
